import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';
import type RouteAssistantSplit from '@alltrails/amplitude/enums/RouteAssistantSplit';
import type { AdvancedElevationChartZoomRange } from '@alltrails/maps/components/AdvancedElevationChart/AdvancedElevationChart';
import { type CustomRouteState, resetState, setRoute } from '@alltrails/maps/redux/customRoute';
import type { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import { isToggledMapContentEntry, mapContentEntriesById, type MapContentId } from '@/components/AdvancedBuilder/mapContent/mapContentRegistry';
import type { MapContentVisibility } from '@/components/AdvancedBuilder/mapContent/mapContentVisibility';
import type { PoiGroupId } from '@/components/AdvancedBuilder/mapContent/poiGroups';

/** Every Route Assistant split but an even one, the kinds that ask for feedback */
export type RouteAssistantFeedbackSplitType = Exclude<RouteAssistantSplit, 'even split'>;

/** Where leaving the builder goes: back to where the user came from, or the matching explore custom route page */
export type AdvancedBuilderLeaveDestination = 'exit' | 'standardBuilder';

export type AdvancedBuilderState = {
  contentVisibility: MapContentVisibility;
  /** Exclusions rather than inclusions, so a group added later is on for everyone. */
  excludedPoiGroups: PoiGroupId[];
  /** Stored only while off, so a later default change still reaches anyone who never touched it. */
  showGradeShading?: boolean;
  /**
   * The elevation chart's drag-to-zoom range, with the selection it was dragged against. Never persisted: it describes a
   * view of one route, not a preference.
   */
  elevationChartZoom?: { range: AdvancedElevationChartZoomRange; segmentIndex?: number };
  /** The nearby trail whose card was clicked, shown in a map window. Never persisted. */
  selectedTrail?: TrailSearchResult;
  /** Whether the selected segment's details are open in a map window. Never persisted. */
  isEditingSegment?: boolean;
  /** Whether the route's title, description and privacy are open in a map window. Never persisted. */
  isEditingRouteDetails?: boolean;
  /** Whether the left panel is collapsed. Never persisted. */
  isLeftPanelCollapsed?: boolean;
  /** The kind of Route Assistant split the left panel is asking for feedback on. Never persisted. */
  routeAssistantFeedbackSplitType?: RouteAssistantFeedbackSplitType;
  /** Set by a header control leaving with unsaved changes, for the navigation guard to ask before it goes. Never persisted. */
  leaveRequest?: AdvancedBuilderLeaveDestination;
};

/** What `advancedBuilderStorage` writes to localStorage, and so all a hydration can restore. */
export type PersistedAdvancedBuilder = Pick<AdvancedBuilderState, 'contentVisibility' | 'excludedPoiGroups' | 'showGradeShading'>;

const defaultShowGradeShading = true;

export const initialState: AdvancedBuilderState = {
  contentVisibility: {},
  excludedPoiGroups: []
};

export const advancedBuilderSlice = createSlice({
  name: 'advancedBuilder',
  initialState,
  reducers: {
    toggleContentVisibility: (state, action: PayloadAction<MapContentId>) => {
      const entry = mapContentEntriesById[action.payload];
      if (isToggledMapContentEntry(entry)) {
        return;
      }
      // Back at the default, delete the key so a later default change still applies
      const isVisible = !(state.contentVisibility[entry.id] ?? entry.defaultVisible);
      if (isVisible === entry.defaultVisible) {
        delete state.contentVisibility[entry.id];
      } else {
        state.contentVisibility[entry.id] = isVisible;
      }
    },
    setExcludedPoiGroups: (state, action: PayloadAction<PoiGroupId[]>) => {
      state.excludedPoiGroups = action.payload;
    },
    toggleGradeShading: state => {
      if (state.showGradeShading ?? defaultShowGradeShading) {
        state.showGradeShading = !defaultShowGradeShading;
      } else {
        delete state.showGradeShading;
      }
    },
    setElevationChartZoom: (state, action: PayloadAction<NonNullable<AdvancedBuilderState['elevationChartZoom']>>) => {
      state.elevationChartZoom = action.payload;
    },
    clearElevationChartZoom: state => {
      delete state.elevationChartZoom;
    },
    setSelectedTrail: (state, action: PayloadAction<TrailSearchResult>) => {
      state.selectedTrail = action.payload;
    },
    clearSelectedTrail: state => {
      delete state.selectedTrail;
    },
    openSegmentEditor: state => {
      state.isEditingSegment = true;
    },
    closeSegmentEditor: state => {
      delete state.isEditingSegment;
    },
    openRouteDetailsEditor: state => {
      state.isEditingRouteDetails = true;
    },
    closeRouteDetailsEditor: state => {
      delete state.isEditingRouteDetails;
    },
    setLeftPanelCollapsed: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        state.isLeftPanelCollapsed = true;
      } else {
        delete state.isLeftPanelCollapsed;
      }
    },
    // Every split opens the panel, even one made while an earlier split's card is still showing
    openRouteAssistantFeedback: (state, action: PayloadAction<RouteAssistantFeedbackSplitType>) => {
      state.routeAssistantFeedbackSplitType = action.payload;
      delete state.isLeftPanelCollapsed;
    },
    closeRouteAssistantFeedback: state => {
      delete state.routeAssistantFeedbackSplitType;
    },
    requestLeave: (state, action: PayloadAction<AdvancedBuilderLeaveDestination>) => {
      state.leaveRequest = action.payload;
    },
    clearLeaveRequest: state => {
      delete state.leaveRequest;
    },
    hydrateAdvancedBuilder: (state, action: PayloadAction<Partial<PersistedAdvancedBuilder>>) => ({ ...state, ...action.payload })
  },
  extraReducers: builder => {
    // A new route, or the same one reloaded, shares no view with the last. The selection may not change on the way (it is
    // often empty on both sides), so the listener that clears on a selection change would not catch this. Feedback on a
    // split describes the route it was made on, which a save's setRoute still is.
    builder.addMatcher(isAnyOf(resetState, setRoute), (state, action) => {
      delete state.elevationChartZoom;
      if (!(setRoute.match(action) && action.payload.isSaved)) {
        delete state.routeAssistantFeedbackSplitType;
      }
    });
  }
});

export const {
  clearElevationChartZoom,
  clearLeaveRequest,
  clearSelectedTrail,
  closeRouteAssistantFeedback,
  closeRouteDetailsEditor,
  closeSegmentEditor,
  hydrateAdvancedBuilder,
  openRouteAssistantFeedback,
  openRouteDetailsEditor,
  openSegmentEditor,
  requestLeave,
  setElevationChartZoom,
  setExcludedPoiGroups,
  setLeftPanelCollapsed,
  setSelectedTrail,
  toggleContentVisibility,
  toggleGradeShading
} = advancedBuilderSlice.actions;

export const selectShowGradeShading = (state: { advancedBuilder: AdvancedBuilderState }) =>
  state.advancedBuilder.showGradeShading ?? defaultShowGradeShading;

// The listener clears a range in a second dispatch after the selection moves; matching the segment here means nothing
// reads the old range against the new selection, however that dispatch is timed. A segment edit has no such guard: the
// chart redraws the new distances either way, and the stale range lasts only until that dispatch.
export const selectElevationChartZoomRange = (state: { advancedBuilder: AdvancedBuilderState; customRoute: CustomRouteState }) => {
  const zoom = state.advancedBuilder.elevationChartZoom;
  return zoom && zoom.segmentIndex === state.customRoute.activeSegmentIndex ? zoom.range : undefined;
};

export default advancedBuilderSlice.reducer;
